import { API_BASE_URL } from "../../constants/index"; // Base URL for the API
import HttpInterface from "../../helpers/HttpInterface"; // Helper class for making HTTP requests
import {
  CreateCarrierConfiguration,
  UpdateCarrierConfiguration,
} from "../../Models/CarrierConfiguration";

import { defaultFetchParam, FetchParam } from "../../Models/FetchParam"; // Fetch parameters model and default values
import { buildAPIUrl } from "../../utilities/api"; // Utility function to build the API URL with query parameters

export class CarrierConfigurationsService {
  private httpProxy: HttpInterface; // Instance of the HttpInterface to make HTTP requests

  constructor() {
    this.httpProxy = new HttpInterface(); // Creates an instance of the HttpInterface to handle requests
  }

  /**
   * Fetches all carrierConfigurations from the carrierConfiguration service API.
   *
   * @param params The query parameters for the API request. Defaults to `defaultFetchParam` if not provided.
   * @returns A promise that resolves with the API response containing the list of carrierConfigurations.
   */
  all(params: FetchParam = defaultFetchParam): Promise<any> {
    const requestOptions = {
      method: "GET", // HTTP method for the request
    };

    // Builds the complete API URL using the base URL, endpoint, and parameters
    return this.httpProxy.fetch(
      buildAPIUrl(
        `${API_BASE_URL}/contract-service/carrier-configurations`,
        params
      ),
      requestOptions
    );
  }

  /**
   * Fetches a specific carrierConfiguration by its ID from the API.
   *
   * @param id The unique identifier of the carrierConfiguration to fetch.
   * @returns A promise that resolves with the API response containing the carrierConfiguration details.
   */
  get(id: string): Promise<any> {
    const requestOptions = {
      method: "GET", // HTTP method for the request
    };

    // Constructs the URL for fetching a specific carrierConfiguration by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/contract-service/carrier-configurations/${id}`,
      requestOptions
    );
  }

  /**
   * Creates a new carrierConfiguration in the carrierConfiguration service API.
   *
   * @param carrierConfiguration The details of the carrierConfiguration to create.
   * @returns A promise that resolves with the API response confirming the creation of the carrierConfiguration.
   */
  create(carrierConfiguration: CreateCarrierConfiguration): Promise<any> {
    const requestOptions = {
      method: "POST", // HTTP method for the request
      body: JSON.stringify(carrierConfiguration), // Sends the carrierConfiguration data as JSON in the request body
    };

    // Sends a POST request to create the new carrierConfiguration
    return this.httpProxy.fetch(
      `${API_BASE_URL}/contract-service/carrier-configurations`,
      requestOptions
    );
  }

  /**
   * Updates an existing carrierConfiguration in the carrierConfiguration service API.
   *
   * @param carrierConfiguration The updated details of the carrierConfiguration.
   * @returns A promise that resolves with the API response confirming the update.
   */
  edit(carrierConfiguration: UpdateCarrierConfiguration): Promise<any> {
    const requestOptions = {
      method: "PUT", // HTTP method for the request
      body: JSON.stringify(carrierConfiguration), // Sends the updated carrierConfiguration data as JSON in the request body
    };

    // Sends a PUT request to update the specific carrierConfiguration by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/contract-service/carrier-configurations/${carrierConfiguration.id}`,
      requestOptions
    );
  }

  /**
   * Deletes a carrierConfiguration from the carrierConfiguration service API.
   *
   * @param id The unique identifier of the carrierConfiguration to delete.
   * @returns A promise that resolves with the API response confirming the deletion.
   */
  delete(id: string): Promise<any> {
    const requestOptions = {
      method: "DELETE", // HTTP method for the request
    };

    // Sends a DELETE request to remove the specific carrierConfiguration by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/contract-service/carrier-configurations/${id}`,
      requestOptions
    );
  }
}
